import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Colors } from 'modules/theme';
import InfoTooltip from './InfoTooltip';

const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 0.7; }
  100% { opacity: 0.4; }
`;

const Card = styled.div`
  flex: 1;
  min-width: 200px;
  padding: 16px 18px;
  background: ${Colors.white};
  border: 1px solid ${({ accent }) => (accent === 'red' ? '#F5C6C6' : Colors.gray2)};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ accent }) => (accent === 'red' ? Colors.red : Colors.gray4)};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${Colors.gray};
`;

const Value = styled.div`
  min-height: 28px;
  display: flex;
  align-items: baseline;
`;

const Skeleton = styled.div`
  height: 28px;
  width: 80px;
  border-radius: 4px;
  background: ${Colors.gray2};
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const Footer = styled.div`
  font-size: 11px;
  color: ${Colors.gray};
  font-weight: 500;
`;

const StatCard = ({ label, tooltip, loading, children, footer, accent }) => (
  <Card accent={accent}>
    <LabelRow>
      {label}
      {tooltip ? <InfoTooltip content={tooltip} /> : null}
    </LabelRow>
    <Value>{loading ? <Skeleton /> : children}</Value>
    {footer ? <Footer>{footer}</Footer> : null}
  </Card>
);

StatCard.propTypes = {
  accent: PropTypes.oneOf(['default', 'red']),
  children: PropTypes.node,
  footer: PropTypes.node,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  tooltip: PropTypes.string,
};

StatCard.defaultProps = {
  accent: 'default',
  children: null,
  footer: null,
  loading: false,
  tooltip: null,
};

export default StatCard;
